import { useCallback, useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { CoSignPermissionMetadata, CoSignRequirementReason, UUID } from "@/core/types";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Switch2 } from "@/ui2/molecules/Switch";
import { useCosignSupervisors } from "@/features/cosign/api/queries/useCosignSupervisors";
import { useUpdateUserPermissions } from "@/features/permissions/api/mutations/useUpdateUserPermissions";
import { useUserPermissions } from "@/features/permissions/api/queries/useUserPermissions";

import { AssignedSupervisor } from "./components/AssignedSupervisor";
import { CoSignFormValues } from "./(modal)/schema";
import { EnabledByLabel } from "./components/EnabledByLabel";
import { RequireCoSignModal } from "./(modal)/RequireCoSignModal";
import { RequireCoSignSkeleton } from "./components/RequireCoSignSkeleton";
import { showCoSignToast } from "./helpers/toast-copy";

type Props = {
  isDisabled?: boolean;
  isRequired?: boolean;
  metadata: CoSignPermissionMetadata | null;
  providerName: string;
  userId: UUID;
};

function RequireCoSign({ isDisabled, isRequired, metadata, providerName, userId }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState<
    Partial<CoSignFormValues> | undefined
  >(undefined);
  const { isPending, mutate: updatePermissions } = useUpdateUserPermissions(userId, providerName);

  useCosignSupervisors(); // prefetch supervisors

  const openModal = useCallback(() => {
    if (isPending) return;

    setModalInitialValues(
      metadata
        ? {
            requirementReason: metadata.requirement_reason,
            requirementReasonDetails: metadata.requirement_reason_details ?? "",
            supervisorId: metadata.assigned_supervisor_id,
          }
        : undefined,
    );
    setIsModalOpen(true);
  }, [metadata, isPending]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleToggle = useCallback(
    (value: boolean) => {
      if (isPending) return;

      if (value) return openModal();

      updatePermissions(
        { co_sign: { is_required: false } },
        { onSuccess: () => showCoSignToast({ isCoSignRequired: false, providerName }) },
      );
    },
    [openModal, providerName, updatePermissions, isPending],
  );

  const handleApprove = useCallback(
    (data: CoSignFormValues) => {
      updatePermissions(
        {
          co_sign: {
            assigned_supervisor_id: data.supervisorId as UUID,
            is_required: true,
            requirement_reason: data.requirementReason as CoSignRequirementReason,
            requirement_reason_details:
              data.requirementReason === "Other" ? data.requirementReasonDetails : null,
          },
        },
        {
          onSuccess: () => {
            closeModal();
            showCoSignToast({ isCoSignRequired: true, providerName });
          },
        },
      );
    },
    [closeModal, providerName, updatePermissions],
  );

  return (
    <>
      <DashboardCard2
        Actions={
          metadata && (
            <Button2 isDisabled={isDisabled} onPress={openModal} size="sm" variant="link">
              Edit
            </Button2>
          )
        }
        className="tw:mb-6"
        classNameChildren="tw:flex tw:flex-col tw:gap-4"
        data-public="true"
        title="Require Co-Sign"
      >
        <Switch2
          className="tw:gap-5.5"
          isDisabled={isDisabled}
          isSelected={isRequired}
          onChange={handleToggle}
        >
          <div className="tw:flex tw:flex-col tw:gap-2">
            <Heading2 as="h3" size={1}>
              Co-Sign Required
            </Heading2>

            <p className="body-md">
              All clinical notes from this provider will need co-signature from an approved
              supervisor before being finalized.
            </p>
          </div>
        </Switch2>

        {metadata && (
          <div className="tw:ml-18.5 tw:flex tw:flex-col tw:gap-4">
            <EnabledByLabel
              requirementReason={metadata.requirement_reason}
              requirementReasonDetails={metadata?.requirement_reason_details}
              stateUpdatedAt={metadata.state_updated_at}
              stateUpdatedByName={metadata.state_updated_by_name}
            />

            <AssignedSupervisor assignedSupervisorName={metadata.assigned_supervisor_name} />
          </div>
        )}
      </DashboardCard2>

      <RequireCoSignModal
        initialValues={modalInitialValues}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleApprove}
        userId={userId}
      />
    </>
  );
}

export function RequireCoSignGate({
  isDisabled,
  providerName,
  userId,
}: {
  isDisabled: boolean;
  providerName: string;
  userId: UUID;
}) {
  const { data: permissions, isError, isLoading } = useUserPermissions({ userId });

  if (isLoading) return <RequireCoSignSkeleton />;
  if (isError || !permissions) return null;

  const { co_sign } = permissions;

  const metadata = co_sign.is_required ? co_sign : null;

  return (
    <RequireCoSign
      isDisabled={isDisabled}
      isRequired={co_sign.is_required}
      metadata={metadata}
      providerName={providerName}
      userId={userId}
    />
  );
}
